import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';
import logo from '@/assets/swell-breaks-logo.png';
import { useAuth } from '@/contexts/AuthContext';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/trip-planner', label: 'Trip Planner' },
    { href: '/destinations', label: 'Destinations' },
    { href: '/media', label: 'Media' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-xl border-b border-foreground/20">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src={logo}
              alt="Swell Breaks"
              className="h-12 w-12 group-hover:opacity-80 transition-opacity"
              style={{ filter: 'brightness(0) saturate(100%) opacity(1)' }}
            />
            <span className="font-serif text-2xl font-semibold text-foreground tracking-tight">
              Swell Breaks
            </span>
          </Link>

          {/* Desktop Navigation - More spacious */}
          <nav className="hidden md:flex items-center gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300",
                  isActive(link.href)
                    ? "bg-primary/15 text-primary"
                    : "text-foreground/80 hover:text-foreground hover:bg-card/50"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Auth Buttons - Refined */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Link to="/my-swell-breaks">
                  <Button variant="ghost" size="sm" className="gap-2 rounded-full px-5">
                    <User className="h-4 w-4" />
                    My Swell Breaks
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 rounded-full px-4"
                  onClick={async () => { await signOut(); navigate('/'); }}
                >
                  <LogOut className="h-4 w-4" />
                  Sign out
                </Button>
              </>
            ) : (
              <Link to="/auth">
                <Button variant="ghost" size="sm" className="gap-2 rounded-full px-5">
                  <User className="h-4 w-4" />
                  Log in / Sign up
                </Button>
              </Link>
            )}
            <Link to="/trip-planner">
              <Button variant="default" size="sm" className="rounded-full px-6">
                Start Planning
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-5 w-5 text-foreground" />
            ) : (
              <Menu className="h-5 w-5 text-foreground" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/50 animate-in slide-in-from-top-2">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "px-4 py-3 rounded-lg text-sm font-medium transition-all",
                    isActive(link.href)
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  )}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-3 pt-3 border-t border-border/50 flex flex-col gap-2">
                {user ? (
                  <>
                    <Link to="/my-swell-breaks" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full gap-2">
                        <User className="h-4 w-4" />
                        My Swell Breaks
                      </Button>
                    </Link>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full gap-2"
                      onClick={async () => { setMobileMenuOpen(false); await signOut(); navigate('/'); }}
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </Button>
                  </>
                ) : (
                  <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full gap-2">
                      <User className="h-4 w-4" />
                      Log in / Sign up
                    </Button>
                  </Link>
                )}
                <Link to="/trip-planner" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="default" size="sm" className="w-full">
                    Start Planning
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
